import { ModelToRun } from '@/scenes/engine/common/constants';
import { customAmplitude } from '@/utils/customAmplitude';
import store from '@/store/store';
import { selectTemplateConfig } from '@/store/slices/templates/selectors';
import api from '../api';
import { moderateAi, type AiModerationReason } from './moderationService';
import { MediaImageRepositoryProcessingDOM } from '@/scenes/engine/objects/media-repository/MediaImageRepositoryProcessingDOM';
import { geminiAspectRatioString, geminiImageSizeString } from '@/services/gemini/geminiHelpers';
import { getOpenAiEditImageQuality, getOpenAiEditSize } from '@/services/openai/openAiImageEditHelpers';
import { ActionItem } from '@/scenes/Editor/components/FloatingImprover/ActionItem';
import {
  IMAGE_EDIT_SUGGESTIONS_PROMPT_ID,
  IMAGE_EDIT_SUGGESTIONS_BG_PROMPT_ID,
  IMAGE_EDIT_SUGGESTIONS_PHOTOSHOOT_PROMPT_ID,
  MODERATION_INPUT_MAX_EDGE,
  MODERATION_JPEG_QUALITY,
} from '@/scenes/engine/common/promptConstants';
import { ImageQuality } from '@/store/slices/aiFashion/types';
import { showTextModal } from '@/components/ShowModal/textModalConfigs';

const EDIT_WITH_AI_INPUT_MAX_EDGE = 1024;
const EDIT_WITH_AI_INPUT_JPEG_QUALITY = 0.8;

const imageProcessing = new MediaImageRepositoryProcessingDOM();

async function normalizeEditWithAiInputs(imageData: string[]): Promise<string[]> {
  return Promise.all(
    imageData.map(async (image) => {
      try {
        return await imageProcessing.resizeBase64ToMaxEdgeSizeAsJpeg(
          image,
          EDIT_WITH_AI_INPUT_MAX_EDGE,
          EDIT_WITH_AI_INPUT_JPEG_QUALITY
        );
      } catch (error) {
        console.error('Failed to normalize Edit with AI input image:', error);
        return image;
      }
    })
  );
}

async function runOpenAiEdit(
  prompt: string,
  imageData: string[],
  imageMask: string,
  aspectRatio?: number,
  quality?: number,
  abortSignal?: AbortSignal
): Promise<{success: boolean, image?: string}> {
  try {
    const hasMask = Boolean(imageMask?.trim());
    const openaiResponse = await api.openApiEditWithImage(
      prompt,
      imageData,
      imageMask,
      getOpenAiEditSize(aspectRatio, hasMask),
      getOpenAiEditImageQuality(quality),
      abortSignal
    );
    if (!openaiResponse.success) {
      return { success: false, image: null };
    }
    const inputBase64Image = openaiResponse?.result?.data?.[0]?.b64_json || null;
    const base64Image = `data:image/png;base64,${inputBase64Image}`;
    return { success: true, image: base64Image };
  } catch (error: any) {
    console.error('OpenAI enhanceImage error:', error?.response?.data || error);
    throw error?.response?.data || error;
  }
}

/**
 * Describe an image using OpenAI or Gemini
 */
export async function describeImage(
  prompt: string,
  provider: ModelToRun,
  imageData?: string,
  abortSignal?: AbortSignal
): Promise<string> {
  if (provider === ModelToRun.OPENAI) {
    try {
        // const openaiResponse = await openAIDescribeItemsOnImage(prompt, imageData)
        const openaiResponse = await api.openApiWithImage(prompt, imageData, abortSignal)
        return openaiResponse.data.choices[0].message.content;;
    } catch (error: any) {
      error.code !== "ERR_CANCELED" && console.error('OpenAI describeImage error:', error?.response?.data || error);
      return 'Failed to describe the image (OpenAI).';
    }
  }

  if (provider === ModelToRun.GEMINI) {
    try {
      const geminiResponse = await api.gemini(prompt, imageData, abortSignal)
      let text = geminiResponse.result.parts[geminiResponse.result.parts.length - 1].text;
      return text;
    } catch (error: any) {
      console.error('Gemini describeImage error:', error?.response?.data || error);
      return 'Failed to describe the image (Gemini).';
    }
  }

  return 'Provider not recognized.';
}

export interface GenerateImageAnalyticsOptions {
  type?: 'Suggested' | 'Custom' | 'Try again' | 'Restyle' | 'AI_models'
  prompt?: string
  templateId?: number
  templateCategoryId?: number | string
  numberOfAttachments?: number
}

export interface GenerateImageSafetyOptions {
  skipNsfwCheck?: boolean
  skipPromptSafety?: boolean
}

const DEFAULT_GEMINI_IMAGE_MODEL = 'gemini-2.5-flash-image';

async function runGeminiGenerate(
  prompt: string,
  images: string[],
  abortSignal?: AbortSignal,
  aspectRatio?: number,
  model?: string,
  resolution?: ImageQuality,
): Promise<{success: boolean, image?: string}> {
  const response = await api.gemini(prompt, images, abortSignal, {
    model: model || DEFAULT_GEMINI_IMAGE_MODEL,
    responseModalities: ['Image'],
    aspectRatio: geminiAspectRatioString(aspectRatio),
    imageSize: geminiImageSizeString(resolution),
  });
  if (!response.success) {
    return {success: false, image: null};
  }
  const generatedImage = response.result?.parts?.find((part: any) => part.inlineData?.data)?.inlineData?.data;
  if (!generatedImage) {
    return {success: false, image: null};
  }
  return {success: true, image: `data:image/png;base64,${generatedImage}`};
}

export async function generateImageBaseOnImage(
  imageData: string[],
  prompt: string,
  provider: ModelToRun,
  imageMask: string,
  aspectRatio?: number,
  quality?: number,
  abortSignal?: AbortSignal,
  analyticsOptions?: GenerateImageAnalyticsOptions,
  resolution?: ImageQuality,
  safetyOptions?: GenerateImageSafetyOptions,
): Promise<{success: boolean, image?: string}> {
  const normalizedImages = await normalizeEditWithAiInputs(imageData);

  if (!safetyOptions?.skipNsfwCheck) {
    const moderationSource = analyticsOptions?.type || 'Edit'
    const logBlockedContent = (reason: AiModerationReason | null) => {
      customAmplitude('NSFW', {
        Source: moderationSource,
        Query: prompt,
        Reason: reason || 'other',
      })
    }

    try {
      const moderationImage = normalizedImages[0]
        ? await imageProcessing.resizeBase64ToMaxEdgeSizeAsJpeg(
            normalizedImages[0],
            MODERATION_INPUT_MAX_EDGE,
            MODERATION_JPEG_QUALITY
          )
        : null

      const moderationResult = await moderateAi(prompt, moderationImage, {
        skipPromptSafety: safetyOptions?.skipPromptSafety,
        abortSignal,
      })

      if (moderationResult.isFlagged) {
        logBlockedContent(moderationResult.reason)
        showTextModal('NSFW_ERROR')
        return { success: false, image: null }
      }
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED' || abortSignal?.aborted) {
        throw error
      }

      console.error('AI moderation failed:', error?.response?.data || error)
      logBlockedContent('other')
      showTextModal('NSFW_ERROR')
      return { success: false, image: null }
    }
  }

  // Track analytics if options provided
  if (analyticsOptions) {
    const state = store.getState();
    const templateConfig = selectTemplateConfig(state);
    
    customAmplitude('Edit with AI', {
      Type: analyticsOptions.type || 'Suggested',
      Prompt: analyticsOptions.prompt || prompt,
      Model: provider,
      'Template Id': analyticsOptions.templateId || templateConfig?.templateId || null,
      'Template Category Id': analyticsOptions.templateCategoryId || templateConfig?.templateCategory || null,
      'Number of attachments': analyticsOptions.numberOfAttachments ?? 1,
    });
  }

  if (provider === ModelToRun.OPENAI) {
    return runOpenAiEdit(prompt, normalizedImages, imageMask, aspectRatio, quality, abortSignal)
  } else if (provider === ModelToRun.GEMINI) {
    return runGeminiGenerate(prompt, normalizedImages, abortSignal, aspectRatio)
  } else if (provider === ModelToRun.GEMINI_3_1) {
    return runGeminiGenerate(prompt, normalizedImages, abortSignal, aspectRatio, 'gemini-3.1-flash-image-preview', resolution)
  }
  else if (provider === ModelToRun.FLUX) {
    const fluxResponse = await api.fluxKontextPro(prompt, normalizedImages[0], abortSignal)
    if (!fluxResponse) {
      return {success: false, image: null};
    }
    // let resizeLatestBlob = await MediaImageRepositoryProvider.getEditingProjectInstance().getDataBlob(fluxResponse.result);
    return {success: true, image: fluxResponse};
  }
  else if (provider === ModelToRun.QWEN) {
    // Remove base64 prefix from images
    const regExBase64Prefix = new RegExp('^data:image/[^;]+;base64,', "g")
    const imagesWithoutPrefix: string[] = normalizedImages.map(image => image.replace(regExBase64Prefix, ''))
    
    const qwenResponse = await api.qwenImage(prompt, imagesWithoutPrefix, abortSignal)
    if (!qwenResponse) {
      return {success: false, image: null};
    }
    
    const base64Image = `data:image/png;base64,${qwenResponse}`;
    return {success: true, image: base64Image};
  }
  else {
    return runOpenAiEdit(prompt, normalizedImages, imageMask, aspectRatio, quality, abortSignal)
  }
}

const SUGGESTIONS_RESIZE_EDGE = 256;
const SUGGESTIONS_JPEG_QUALITY = 0.8;

/**
 * Fetch AI editing suggestions for an image. Pure service call — no editor/canvas dependency.
 * Resizes to 256px, calls the suggestions API, returns prompt items.
 */
export async function fetchImageSuggestions(
  base64Image: string,
  suggestionType: 'edit' | 'background' | 'photoshoot' = 'edit',
  abortSignal?: AbortSignal
): Promise<ActionItem[]> {
  const resizedImage = await imageProcessing.resizeBase64ToMaxEdgeSizeAsJpeg(
    base64Image,
    SUGGESTIONS_RESIZE_EDGE,
    SUGGESTIONS_JPEG_QUALITY
  );
  const currentLanguage = localStorage.getItem('language') || 'en';

  const promptId =
    suggestionType === 'background'
      ? IMAGE_EDIT_SUGGESTIONS_BG_PROMPT_ID
      : suggestionType === 'photoshoot'
        ? IMAGE_EDIT_SUGGESTIONS_PHOTOSHOOT_PROMPT_ID
        : IMAGE_EDIT_SUGGESTIONS_PROMPT_ID;

  const result = await api.imageEditSuggestionsGemini(
    resizedImage,
    promptId,
    currentLanguage,
    abortSignal
  );

  return result && result.length > 0 ? result : [];
}
